import {
  Box,
  Flex,
  Heading,
  HStack,
  Icon,
  Image,
  Progress,
  Stack,
  Text,
  VStack,
} from "@chakra-ui/react"
import { type FC } from "react"

import BlurFade from "@/components/Animations/BlurFade"
import { BriefcaseIcon, MapMarkerIcon } from "@/components/icons"
import MarkdownParser from "@/components/MarkdownParser"
import { type PersonaBlock as PersonaBlockType } from "@/types/database"

import {
  type ContentBlockTitleNumberProps,
  formatContentBlockTitleNumber,
} from "./ContentBlockTitle"

interface PersonaBlockProps extends ContentBlockTitleNumberProps {
  block: PersonaBlockType
}

const PersonaBlock: FC<PersonaBlockProps> = ({ block, titleNumber }) => {
  const {
    about,
    avatar,
    chart_items,
    chart_title,
    darker_bg,
    frustrations,
    full_name,
    goals,
    location,
    occupation,
    subtitle,
    title = "Persona(s)",
  } = block
  const formattedTitleNumber = formatContentBlockTitleNumber(titleNumber)
  const trimmedTitle = title?.trim()
  const trimmedDescription = subtitle?.trim()
  const hasTitle = Boolean(trimmedTitle)
  const hasDescription = Boolean(
    trimmedDescription !== undefined &&
    trimmedDescription !== "" &&
    trimmedDescription !== null
  )
  return (
    <Flex
      align="start"
      bgColor={darker_bg ? "primary.900" : "primary.800"}
      className="persona-block internal-padding"
      color="white"
      direction={{ base: "column" }}
      gap={0}
    >
      {(hasTitle || hasDescription) && (
        <HStack gap={0} mb={8} w="full">
          <VStack alignItems="flex-start" gap={0}>
            {formattedTitleNumber && (
              <Text
                as="span"
                color={{ base: "primary.200", _dark: "primary.200" }}
                fontFamily="mono"
                fontSize="0.8rem"
                fontWeight="medium"
                lineHeight="inherit"
              >
                {formattedTitleNumber}
              </Text>
            )}
            <Heading
              alignItems="baseline"
              as="h2"
              display="flex"
              flexWrap="wrap"
              gap={{ base: 2, md: 3 }}
              size="2xl"
            >
              {title}
            </Heading>
          </VStack>
        </HStack>
      )}
      <Flex
        align="start"
        direction={{ base: "column", md: "row" }}
        gap={10}
        w="full"
      >
        {/* Left Side */}
        <VStack
          flexBasis={{ base: "auto", md: "0" }}
          flexGrow={1}
          gap={10}
          width={{ base: "100%", md: "50%" }}
        >
          <Stack
            align="start"
            direction={{ base: "column", lg: "row" }}
            gap={{ base: 4, lg: 10 }}
            w="full"
          >
            <BlurFade flexGrow={"inherit"} inView w="auto">
              <Image
                alt={full_name}
                borderRadius="xl"
                boxSize="250px"
                flexShrink={0}
                objectFit="cover"
                src={`${process.env.DIRECTUS_ASSETS_URL}${avatar.id}&quality=100&width=250`}
              />
            </BlurFade>
            <VStack align="start" alignItems="stretch" gap={{ base: 4, lg: 6 }}>
              <Box flexGrow={1}>
                <BlurFade inView>
                  <VStack alignItems="flex-start" gap={0}>
                    <Text
                      fontSize={{ base: "xl", lg: "2xl", xl: "3xl" }}
                      fontWeight="bold"
                      lineClamp="2"
                      truncate
                      whiteSpace="break-spaces"
                    >
                      {full_name}
                    </Text>
                  </VStack>
                </BlurFade>
              </Box>

              <Stack
                direction={{ base: "row" }}
                gap={{ base: 4, lg: 6 }}
                mt={{ base: 0, lg: 4 }}
              >
                <BlurFade inView>
                  <VStack align="start" gap={0}>
                    <Icon as={BriefcaseIcon} mb={2} size={"lg"} />
                    <Text fontSize={{ base: "xs", lg: "sm" }} opacity={0.6}>
                      Occupation
                    </Text>
                    <Text
                      fontSize={{ base: "xs", lg: "sm" }}
                      fontWeight="medium"
                    >
                      {occupation}
                    </Text>
                  </VStack>
                </BlurFade>
                <BlurFade inView>
                  <VStack align="start" gap={0}>
                    <Icon as={MapMarkerIcon} mb={2} size={"lg"} />
                    <Text fontSize={{ base: "xs", lg: "sm" }} opacity={0.6}>
                      Location
                    </Text>
                    <Text
                      fontSize={{ base: "xs", lg: "sm" }}
                      fontWeight="medium"
                    >
                      {location}
                    </Text>
                  </VStack>
                </BlurFade>
              </Stack>
            </VStack>
          </Stack>
          <VStack alignItems="flex-start" w="full">
            {chart_title && (
              <Text fontSize="xl" fontWeight="bold" mb={4}>
                {chart_title}
              </Text>
            )}
            <VStack align="start" alignItems="flex-start" gap={2} w="full">
              {chart_items.map((item: { name: string; value: number }) => (
                <Box key={item.name} w="full">
                  <BlurFade inView>
                    <Progress.Root
                      animated
                      colorPalette={"secondary"}
                      defaultValue={item.value}
                      mb={4}
                      striped
                      variant="subtle"
                      w="full"
                    >
                      <Progress.Label mb="2">{item.name}</Progress.Label>
                      <Progress.Track
                        _dark={{
                          bg: "secondary.900/20",
                        }}
                        bg="secondary.200/10"
                        rounded="full"
                      >
                        <Progress.Range color="secondary.500" rounded="full" />
                      </Progress.Track>
                    </Progress.Root>
                  </BlurFade>
                </Box>
              ))}
            </VStack>
          </VStack>
        </VStack>

        {/* Right Side */}
        <VStack
          alignItems="flex-start"
          flexBasis={{ base: "auto", md: "0" }}
          flexGrow={1}
          gap={8}
          width={{ base: "100%", md: "50%" }}
        >
          <VStack alignItems="flex-start">
            <BlurFade inView>
              <Text fontSize="xl" fontWeight="bold" mb={2}>
                About
              </Text>
            </BlurFade>
            <BlurFade inView>
              <MarkdownParser color="white/60" fontSize="lg" markdown={about} />
            </BlurFade>
          </VStack>

          <VStack alignItems="flex-start">
            <BlurFade inView>
              <Text fontSize="xl" fontWeight="bold" mb={2}>
                Goals
              </Text>
            </BlurFade>
            <BlurFade inView>
              <MarkdownParser color="white/60" fontSize="lg" markdown={goals} />
            </BlurFade>
          </VStack>

          <VStack alignItems="flex-start">
            <BlurFade inView>
              <Text fontSize="xl" fontWeight="bold" mb={2}>
                Frustrations
              </Text>
            </BlurFade>
            <BlurFade inView>
              <MarkdownParser
                color="white/60"
                fontSize="lg"
                markdown={frustrations}
              />
            </BlurFade>
          </VStack>
        </VStack>
      </Flex>
    </Flex>
  )
}

export default PersonaBlock
